import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';
import logo from '../logo.png';
import ThemeToggle from './ThemeToggle';

const Layout = ({ children }) => {
  const location = useLocation();
  const { theme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we are on a product page with video hero (El Mesías)
  const isTransparentHeaderPage = false;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const headerClass = `fixed top-0 w-full z-50 transition-all duration-300 px-6 py-4 flex justify-between items-center ${isScrolled || !isTransparentHeaderPage || isMobileMenuOpen
    ? (theme === 'dark' ? 'bg-dark-bg/90 backdrop-blur-md' : 'bg-white/90 backdrop-blur-md')
    : 'bg-transparent'
    }`;

  const navLinkClass = `text-sm font-bold uppercase tracking-widest hover:opacity-70 transition-opacity ${isScrolled || !isTransparentHeaderPage || isMobileMenuOpen
    ? (theme === 'dark' ? 'text-white' : 'text-charcoal')
    : 'text-white'
    }`;

  const logoClass = `text-2xl font-bold tracking-tighter ${isScrolled || !isTransparentHeaderPage || isMobileMenuOpen
    ? (theme === 'dark' ? 'text-white' : 'text-charcoal')
    : 'text-white'
    }`;

  const mobileMenuLinkClass = `text-2xl font-bold uppercase tracking-widest hover:opacity-70 transition-opacity ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`;

  return (
    <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-dark-bg' : 'bg-off-white'}`}>
      <header className={headerClass}>
        <Link to="/" className="flex items-center space-x-3">
          <img src={logo} alt="FOIROLA Logo" className="h-16 w-16 object-contain hidden md:block" />
          <span className={logoClass}>FOIROLA</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className={navLinkClass}>{t('nav.product')}</Link>
          <Link to="/sobre-nosotros" className={navLinkClass}>{t('nav.about')}</Link>
          <Link to="/contacto" className={navLinkClass}>{t('nav.contact')}</Link>

          <div className="flex items-center space-x-4 ml-4 border-l border-gray-400 pl-4">
            <button
              onClick={toggleLanguage}
              className={navLinkClass}
            >
              {language.toUpperCase()}
            </button>
            <ThemeToggle />
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center z-50">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={navLinkClass}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className={`fixed inset-0 z-40 flex flex-col justify-center items-center ${theme === 'dark' ? 'bg-dark-bg' : 'bg-off-white'}`}>
          <nav className="flex flex-col items-center space-y-8">
            <Link to="/" className={mobileMenuLinkClass}>{t('nav.product')}</Link>
            <Link to="/sobre-nosotros" className={mobileMenuLinkClass}>{t('nav.about')}</Link>
            <Link to="/contacto" className={mobileMenuLinkClass}>{t('nav.contact')}</Link>

            <div className="flex items-center space-x-8 mt-8 pt-8 border-t border-gray-500 w-full justify-center">
              <button
                onClick={toggleLanguage}
                className={mobileMenuLinkClass}
              >
                {language.toUpperCase()}
              </button>
              <ThemeToggle />
            </div>
          </nav>
        </div>
      )}

      <main className="flex-grow pt-14">
        {children}
      </main>

      <footer className={`relative z-10 ${theme === 'dark' ? 'bg-charcoal text-gray-400' : 'bg-white text-gray-600'} py-12 border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>FOIROLA</h3>
            <p className="text-sm font-light">
              {t('footer.designed')} <br />
              {t('footer.inspired')}
            </p>
          </div>
          <div>
            <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>{t('footer.legal')}</h3>
            <ul className="space-y-2 text-sm font-light">
              <li><Link to="/legal#terms" className="hover:underline">{t('footer.terms')}</Link></li>
              <li><Link to="/legal#privacy" className="hover:underline">{t('footer.privacy')}</Link></li>
              <li><Link to="/legal#shipping" className="hover:underline">{t('footer.shipping')}</Link></li>
            </ul>
          </div>
          <div>
            <h3 className={`text-lg font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>{t('footer.social')}</h3>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/foirola.brand/" target="_blank" rel="noopener noreferrer" className={`hover:opacity-70 transition-opacity ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href="mailto:foirola29640@gmail.com" className={`hover:opacity-70 transition-opacity ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-8 border-t border-gray-200 text-center text-xs font-light">
          &copy; {new Date().getFullYear()} Foirola Brand. {t('footer.rights')}
        </div>
      </footer>
    </div>
  );
};

export default Layout;